import { useState, useEffect, useRef } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import "./users.css";

const UserProfile = () => {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [changePassToggle, setChangePassToggle] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [error, setError] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const changePassRef = useRef(null);

  const [form, setForm] = useState({
    nome: "",
    cognome: "",
    descrizione: "",
  });

  const fetchUser = async () => {
    try {
      setLoading(true);
      const res = await api.get("/users/me");
      if (res.status === 200) {
        setUser(res.data);
        setForm({
          nome: res.data.nome || "",
          cognome: res.data.cognome || "",
          descrizione: res.data.descrizione || "",
        });
      }
    } catch (err) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (!userRole || userRole === "TRAINER") {
      alert("Accedi per visualizzare il profilo!");
      navigate("/login");
      localStorage.clear();
    } else {
      fetchUser();
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prevErrors) => {
      if (prevErrors[name]) {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      }
      return prevErrors;
    });
  };

  const validate = () => {
    const inputErrors = {};
    if (!form.nome.trim())
      inputErrors.nome = "Il campo Nome non può essere vuoto!";
    if (!form.cognome.trim())
      inputErrors.cognome = "Il campo Cognome non può essere vuoto!";
    if (!form.descrizione.trim())
      inputErrors.descrizione = "Il campo Descrizione non può essere vuoto!";

    setErrors(inputErrors);
    return Object.keys(inputErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      const payload = {
        nome: form.nome,
        cognome: form.cognome,
        descrizione: form.descrizione,
      };
      const res = await api.put(`/users`, payload);
      if (res.status === 200) {
        toast.success("Profilo aggiornato con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          transition: Bounce,
        });
        setUser(res.data);
        setEditMode(false);
        fetchUser();
      }
    } catch (err) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm({
      nome: user.nome || "",
      cognome: user.cognome || "",
      descrizione: user.descrizione || "",
    });
    setErrors({});
    setEditMode(false);
  };

  const isValid = () => {
    const inputErrors = {};
    if (currentPassword.trim() === "") {
      inputErrors.currentPassword =
        "Il campo Password attuale non può essere vuoto!";
      return false;
    }

    if (newPassword.trim() === "") {
      inputErrors.newPassword = "Il campo Nuova password non può essere vuoto!";
      return false;
    }

    if (confirmNewPassword.trim() === "") {
      inputErrors.confirmNewPassword =
        "Il campo Conferma nuova password non può essere vuoto!";
      return false;
    }

    if (newPassword !== confirmNewPassword) {
      inputErrors.confirmNewPassword = "Le nuove password non coincidono!";
      return false;
    }

    setError(inputErrors);
    return Object.keys(inputErrors).length === 0;
  };

  const changePassword = async () => {
    if (!isValid()) return;

    try {
      setLoading(true);
      const response = await api.post("/reset-password/self", {
        currentPassword,
        newPassword,
        confirmationPassword: confirmNewPassword,
      });

      if (response.status === 200 || response.data?.status === 200) {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
        setChangePassToggle(false);
        toast.success("Password modificata con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } else if (response.status === 400) {
        toast.error("Le nuove password non coincidono!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : "";
    const last = lastName ? lastName.charAt(0).toUpperCase() : "";
    return first + last;
  };

  return (
    <div className="user-profile-container">
      {loading && (
        <div className="user-profile-overlay">
          <div className="user-profile-spinner"></div>
        </div>
      )}

      <div className="user-profile-header">
        <div className="user-profile-header-content">
          <button
            className="user-profile-back-btn"
            onClick={() => navigate(-1)}
          >
            <i className="fa-solid fa-arrow-left"></i>
            <span>Indietro</span>
          </button>
          <h1 className="user-profile-title">
            <i className="fa-solid fa-user-circle"></i>
            Profilo Utente
          </h1>
          <div className="user-profile-actions">
            {!editMode && (
              <button
                className="user-profile-edit-btn"
                onClick={() => setEditMode(true)}
              >
                <i className="fa-solid fa-edit"></i>
                <span>Modifica Profilo</span>
              </button>
            )}
            <button
              className="user-profile-password-btn"
              onClick={() => {
                setChangePassToggle(true);
                setTimeout(() => {
                  changePassRef.current?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
            >
              <i className="fa-solid fa-key"></i>
              <span>Cambia Password</span>
            </button>
          </div>
        </div>
      </div>

      <div className="user-profile-content">
        {/* User Profile Card */}
        <div className="user-profile-card">
          <div className="user-profile-avatar-section">
            <div className="user-profile-avatar">
              <div className="user-profile-avatar-initials">
                {getInitials(user.nome, user.cognome)}
              </div>
              <div className="user-profile-status">
                <span className="user-profile-status-badge">
                  <i className="fa-solid fa-circle"></i>
                  Attivo
                </span>
              </div>
            </div>
            <div className="user-profile-info">
              <h2 className="user-profile-name">
                {user.nome} {user.cognome}
              </h2>
              <p className="user-profile-email">
                <i className="fa-solid fa-envelope"></i>
                {user.email || "Email non disponibile"}
              </p>
              <p className="user-profile-role">
                <i className="fa-solid fa-user-tag"></i>
                {user.role || "Ruolo non specificato"}
              </p>
            </div>
          </div>
        </div>

        {/* User Details Card */}
        <div className="user-profile-details-card">
          <div className="user-profile-details-header">
            <h3 className="user-profile-details-title">
              <i className="fa-solid fa-info-circle"></i>
              Informazioni Personali
            </h3>
            {editMode && (
              <div className="user-profile-edit-actions">
                <button
                  className="user-profile-cancel-btn"
                  onClick={handleCancel}
                >
                  <i className="fa-solid fa-times"></i>
                  <span>Annulla</span>
                </button>
                <button className="user-profile-save-btn" onClick={handleSave}>
                  <i className="fa-solid fa-save"></i>
                  <span>Salva Modifiche</span>
                </button>
              </div>
            )}
          </div>

          <div className="user-profile-details-content">
            <div className="user-profile-field">
              <label className="user-profile-field-label">
                <i className="fa-solid fa-user"></i>
                Nome
              </label>
              <div className="user-profile-field-value">
                {editMode ? (
                  <input
                    type="text"
                    name="nome"
                    value={form.nome}
                    onChange={handleChange}
                    className={`user-profile-input ${
                      errors.nome ? "error" : ""
                    }`}
                    placeholder="Inserisci il nome"
                  />
                ) : (
                  <span className="user-profile-text">{user.nome || "—"}</span>
                )}
                {errors.nome && (
                  <span className="user-profile-error">
                    <i className="fa-solid fa-exclamation-triangle"></i>
                    {errors.nome}
                  </span>
                )}
              </div>
            </div>

            <div className="user-profile-field">
              <label className="user-profile-field-label">
                <i className="fa-solid fa-user"></i>
                Cognome
              </label>
              <div className="user-profile-field-value">
                {editMode ? (
                  <input
                    type="text"
                    name="cognome"
                    value={form.cognome}
                    onChange={handleChange}
                    className={`user-profile-input ${
                      errors.cognome ? "error" : ""
                    }`}
                    placeholder="Inserisci il cognome"
                  />
                ) : (
                  <span className="user-profile-text">
                    {user.cognome || "—"}
                  </span>
                )}
                {errors.cognome && (
                  <span className="user-profile-error">
                    <i className="fa-solid fa-exclamation-triangle"></i>
                    {errors.cognome}
                  </span>
                )}
              </div>
            </div>

            <div className="user-profile-field">
              <label className="user-profile-field-label">
                <i className="fa-solid fa-align-left"></i>
                Descrizione
              </label>
              <div className="user-profile-field-value">
                {editMode ? (
                  <textarea
                    name="descrizione"
                    value={form.descrizione}
                    onChange={handleChange}
                    className={`user-profile-textarea ${
                      errors.descrizione ? "error" : ""
                    }`}
                    placeholder="Inserisci una descrizione"
                    rows="3"
                  />
                ) : (
                  <span className="user-profile-text">
                    {user.descrizione || "Nessuna descrizione disponibile"}
                  </span>
                )}
                {errors.descrizione && (
                  <span className="user-profile-error">
                    <i className="fa-solid fa-exclamation-triangle"></i>
                    {errors.descrizione}
                  </span>
                )}
              </div>
            </div>

            <div className="user-profile-field">
              <label className="user-profile-field-label">
                <i className="fa-solid fa-calendar-plus"></i>
                Data Creazione
              </label>
              <div className="user-profile-field-value">
                <span className="user-profile-text">
                  {formatDate(user.dataCreazione) || "—"}
                </span>
              </div>
            </div>

            <div className="user-profile-field">
              <label className="user-profile-field-label">
                <i className="fa-solid fa-calendar-check"></i>
                Ultima Modifica
              </label>
              <div className="user-profile-field-value">
                <span className="user-profile-text">
                  {formatDate(user.dataUltimaModifica) || "—"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        {changePassToggle && (
          <div className="user-profile-password-card" ref={changePassRef}>
            <div className="user-profile-password-header">
              <h3 className="user-profile-password-title">
                <i className="fa-solid fa-key"></i>
                Cambia Password
              </h3>
              <button
                className="user-profile-close-btn"
                onClick={() => setChangePassToggle(false)}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="user-profile-password-content">
              <div className="user-profile-field">
                <label className="user-profile-field-label">
                  <i className="fa-solid fa-lock"></i>
                  Password Attuale
                </label>
                <div className="user-profile-field-value">
                  <div className="password-input-wrapper">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className={`user-profile-input ${
                        error.currentPassword ? "error" : ""
                      }`}
                      placeholder="Inserisci la password attuale"
                    />
                    <button
                      type="button"
                      className="toggle-password-btn"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      aria-label={showCurrentPassword ? "Nascondi password" : "Mostra password"}
                    >
                      <FontAwesomeIcon icon={showCurrentPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {error.currentPassword && (
                    <span className="user-profile-error">
                      <i className="fa-solid fa-exclamation-triangle"></i>
                      {error.currentPassword}
                    </span>
                  )}
                </div>
              </div>

              <div className="user-profile-field">
                <label className="user-profile-field-label">
                  <i className="fa-solid fa-lock"></i>
                  Nuova Password
                </label>
                <div className="user-profile-field-value">
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={`user-profile-input ${
                        error.newPassword ? "error" : ""
                      }`}
                      placeholder="Inserisci la nuova password"
                    />
                    <button
                      type="button"
                      className="toggle-password-btn"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      aria-label={showNewPassword ? "Nascondi password" : "Mostra password"}
                    >
                      <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {error.newPassword && (
                    <span className="user-profile-error">
                      <i className="fa-solid fa-exclamation-triangle"></i>
                      {error.newPassword}
                    </span>
                  )}
                </div>
              </div>

              <div className="user-profile-field">
                <label className="user-profile-field-label">
                  <i className="fa-solid fa-lock"></i>
                  Conferma Nuova Password
                </label>
                <div className="user-profile-field-value">
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmNewPassword ? "text" : "password"}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className={`user-profile-input ${
                        error.confirmNewPassword ? "error" : ""
                      }`}
                      placeholder="Conferma la nuova password"
                    />
                    <button
                      type="button"
                      className="toggle-password-btn"
                      onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                      aria-label={showConfirmNewPassword ? "Nascondi password" : "Mostra password"}
                    >
                      <FontAwesomeIcon icon={showConfirmNewPassword ? faEyeSlash : faEye} />
                    </button>
                  </div>
                  {error.confirmNewPassword && (
                    <span className="user-profile-error">
                      <i className="fa-solid fa-exclamation-triangle"></i>
                      {error.confirmNewPassword}
                    </span>
                  )}
                </div>
              </div>

              <div className="user-profile-password-actions">
                <button
                  className="user-profile-cancel-btn"
                  onClick={() => setChangePassToggle(false)}
                >
                  <i className="fa-solid fa-times"></i>
                  <span>Annulla</span>
                </button>
                <button
                  className="user-profile-save-btn"
                  onClick={changePassword}
                >
                  <i className="fa-solid fa-save"></i>
                  <span>Salva Password</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
